import { z } from "zod"
import type { Selectable } from "kysely"
import type { FiscalYearTarget } from "@generated/types"
import { isAfter, isBefore } from "~/utils/datetime"
import {
  type FiscalConfig,
  fiscalYearBounds,
  fiscalYearOf,
  formatFiscalYear,
} from "~/utils/fiscalCalendar"
import { getStoredFiscalConfig } from "~/stores/fiscal-config-store"
import type { ICollectionSimple } from "~/hooks/useCollections"
import { type BudgetData } from "~/types/budget"
import { calculateBudgetAllocationForYear } from "~/utils/timeAllocation"
import { formatNumber } from "~/utils/formatting"

export type InclusionStatus = "full" | "partial" | "none"

/** The fiscal years the target wizard offers around `currentYear`, inclusive. */
export const fiscalYearTargetWindow = (currentYear: number): { first: number; last: number } => ({
  first: currentYear - 5,
  last: currentYear + 4,
})

export const fiscalYearParamSchema = z
  .string()
  .regex(/^\d{4}$/)
  .transform(Number)

/** The closest of the ascending `years` before and after `year`, whichever exist. */
export const neighbourYears = (
  years: readonly number[],
  year: number,
): { previous: number | undefined; next: number | undefined } => ({
  previous: years.findLast((y) => y < year),
  next: years.find((y) => y > year),
})

export type YearDistribution = {
  year: number
  amount: number
  percentage: number
}

export function isYearInRangeUTC(
  startDate: Date | undefined | null,
  endDate: Date | undefined | null,
  year: number,
  fiscalConfig: FiscalConfig | null = getStoredFiscalConfig(),
): boolean {
  if (!startDate || !endDate) return true
  const { yearStart, yearEnd } = fiscalYearBounds(year, fiscalConfig)

  // Overlap if start <= yearEnd and end >= yearStart
  return !isAfter(startDate, yearEnd) && !isBefore(endDate, yearStart)
}

export function getCollectionInclusionStatusUTC(
  collection: ICollectionSimple,
  year: number,
  fiscalConfig: FiscalConfig | null = getStoredFiscalConfig(),
): InclusionStatus {
  const { rawStartDate: startDate, rawEndDate: endDate } = collection
  if (!startDate || !endDate) return "full"

  const { yearStart, yearEnd } = fiscalYearBounds(year, fiscalConfig)

  // No overlap
  if (isBefore(endDate, yearStart) || isAfter(startDate, yearEnd)) return "none"

  // Fully inside year
  const fullyInside = !isBefore(startDate, yearStart) && !isAfter(endDate, yearEnd)
  if (fullyInside) return "full"

  return "partial"
}

/**
 * Calculate the inclusion status for a group of collections
 * Returns "full" if all collections are fully included, "none" if no collections are included,
 * "partial" if some are included or partially included
 */
export function getGroupInclusionStatusUTC(
  collections: ICollectionSimple[],
  year: number,
): InclusionStatus {
  const validCollections = collections.filter((c) => c !== undefined && c !== null)

  if (validCollections.length === 0) return "none"

  const statuses = validCollections.map((collection) =>
    getCollectionInclusionStatusUTC(collection, year),
  )

  if (statuses.every((status) => status === "full")) return "full"
  if (statuses.every((status) => status === "none")) return "none"

  return "partial"
}

/**
 * Calculate total budget amount for a specific year across multiple budgets
 */
export function calculateTotalBudgetForYear(budgets: BudgetData[], year: number): number {
  return budgets.reduce((sum, budget) => sum + calculateBudgetAllocationForYear(budget, year), 0)
}

/**
 * Calculate how budgets are distributed across years
 */
export function calculateBudgetYearDistribution(budgets: BudgetData[]): YearDistribution[] {
  if (budgets.length === 0) return []

  const yearsSet = new Set<number>()
  for (const budget of budgets) {
    const startYear = fiscalYearOf(budget.startDate)
    const endYear = fiscalYearOf(budget.endDate)
    for (let year = startYear; year <= endYear; year++) {
      yearsSet.add(year)
    }
  }

  const years = Array.from(yearsSet).sort((a, b) => a - b)
  const totalAmount = budgets.reduce((sum, b) => sum + b.amount, 0)

  return years.map((year) => {
    const yearAmount = budgets.reduce(
      (sum, budget) => sum + calculateBudgetAllocationForYear(budget, year),
      0,
    )
    return {
      year,
      amount: yearAmount,
      percentage: totalAmount > 0 ? (yearAmount / totalAmount) * 100 : 0,
    }
  })
}

export type FiscalYearTargetMode = Selectable<FiscalYearTarget>["mode"]

/** Why a new fiscal-year target can't use `year`, or null when it can. */
export const fiscalYearBlocker = ({
  allTaken,
  takenYears,
  year,
  fiscalConfig,
}: {
  allTaken: boolean
  takenYears: ReadonlySet<number>
  year: number
  fiscalConfig?: FiscalConfig | null
}): string | null => {
  if (allTaken) return "Every available fiscal year already has a target."
  if (takenYears.has(year)) return `${formatFiscalYear(year, fiscalConfig)} already has a target.`
  return null
}

/**
 * Largest top-down FY target, in the company's currency. Keeps every amount derived from it
 * (delta, unallocated, growth) far below 2⁵³, where doubles stop being exact to the cent.
 */
export const MAX_FINANCE_TARGET = 1_000_000_000_000

export const financeTargetMaxMessage = (unit?: string) =>
  `Max ${formatNumber(MAX_FINANCE_TARGET)}${unit ? ` ${unit}` : ""}.`

/**
 * What's wrong with the FY target the wizard would save, or null when it's valid. A top-down
 * target must be above 0 and at most `MAX_FINANCE_TARGET`; an empty bottom-up roll-up still saves,
 * since it follows its collections.
 */
export type FiscalYearTargetIssue = "must-be-positive" | "above-max" | "empty-roll-up"

export const fiscalYearTargetIssue = (
  mode: FiscalYearTargetMode,
  target: number,
): FiscalYearTargetIssue | null => {
  if (mode === "top_down" && target > MAX_FINANCE_TARGET) return "above-max"
  if (target > 0) return null
  return mode === "top_down" ? "must-be-positive" : "empty-roll-up"
}

/** Why the wizard can't continue past the target step, or null. An empty roll-up only warns. */
export const fiscalYearTargetBlocker = (
  issue: FiscalYearTargetIssue | null,
  unit: string,
): string | null => {
  if (issue === "must-be-positive") return "Target must be above 0."
  if (issue === "above-max") return financeTargetMaxMessage(unit)
  return null
}
